import { Monitor } from './types';

/**
 * Handles detection of whether a video has been completed in a ui sense. Completion means that
 * the user has watched the video past 95%.
 *
 * ## Why we need two listener attach points
 *
 * The video player iframe (`mojo/app/views/embed_videos/show.html.erb`) posts lifecycle
 * events (`timeupdate`, `ended`, …) via `window.parent.postMessage`. That target is *not*
 * an accident: there are Sega analytics listeners at each iframe layer that has a Sega SDK
 * instance, and each consumes the video event to emit `analytics.track('w:video_play', …)`
 * scoped to its own frame's context (e.g., enriched with `tracking_id` for journeys).
 * Sending to `window.parent` (rather than `window.top`) is what keeps the analytics
 * emission single-fire — sending to both targets would cause both Sega instances to emit
 * the same `w:video_play`, which is the historical FE-24089 double-firing problem.
 *
 * That layout-aware analytics design has a side-effect on the completion gate. Patron
 * renders video content in two layouts:
 *
 *  - **Direct video** (`/embed/video/<uuid>`, depth 2): the video iframe is the content
 *    iframe. `window.parent` from the video IS Patron's top window, so the message
 *    arrives on `window` and Patron's Sega + this `VideoMonitor` both fire.
 *  - **Article-wrapped video** (`/embed/article/<id>` with an inline srcdoc video,
 *    depth 3): `window.parent` from the video is the *article* iframe (which loads its
 *    own Sega SDK and emits `w:video_play` from there). The message never propagates
 *    up to Patron, so a `VideoMonitor` that only listened on Patron's `window` would
 *    never see it — `is_completed` would stay false and the acknowledge button would
 *    never enable. (FE-32231)
 *
 * The fix is purely on Patron's side: attach the same handler to both Patron's `window`
 * and the content iframe's `contentWindow`, so completion fires correctly in either
 * layout while leaving the analytics path untouched. The article-wrap layer is
 * same-origin with Patron (both on the patron host), so attaching a listener on the
 * iframe's `contentWindow` is permitted by the same-origin policy. The `contentWindow`
 * attach is wrapped in try/catch so a future cross-origin content type degrades to the
 * top-window listener instead of throwing.
 *
 * On iframe navigation, `iframe.contentWindow` returns a stable `WindowProxy` while the
 * underlying `Window` is replaced — the listener is bound to the old `Window` and the new
 * document gets nothing. We always re-attach on every `load` event; `addEventListener` is
 * idempotent for the same (target, callback, options), so this is safe on no-op reloads
 * too. The old `Window` is garbage-collected with its document, so we don't have to
 * remove its listener explicitly.
 */
export default class VideoMonitor implements Monitor {
  private messageHandler?: (event: MessageEvent) => void;
  private attachedIframe?: HTMLIFrameElement;
  private iframeAttachHandler?: () => void;

  constructor(readonly onCompleteCallback: (monitor: VideoMonitor) => void) {}

  attach(el: HTMLIFrameElement) {
    if (this.attachedIframe) {
      this.detach();
    }

    const handler = this.handleMessage.bind(this);
    this.messageHandler = handler;
    this.attachedIframe = el;

    window.addEventListener('message', handler);

    this.iframeAttachHandler = () => {
      try {
        el.contentWindow?.addEventListener('message', handler);
      } catch {
        // Cross-origin contentWindow — degrade to top-window listener only.
      }
    };
    if (el.contentDocument?.readyState === 'complete') {
      this.iframeAttachHandler();
    }
    el.addEventListener('load', this.iframeAttachHandler);
  }

  detach() {
    if (this.messageHandler) {
      window.removeEventListener('message', this.messageHandler);
      try {
        this.attachedIframe?.contentWindow?.removeEventListener(
          'message',
          this.messageHandler
        );
      } catch {
        // Cross-origin or otherwise unreachable — nothing to remove.
      }
    }
    if (this.attachedIframe && this.iframeAttachHandler) {
      this.attachedIframe.removeEventListener('load', this.iframeAttachHandler);
    }
    this.attachedIframe = undefined;
    this.iframeAttachHandler = undefined;
    this.messageHandler = undefined;
  }

  protected handleMessage(event: MessageEvent) {
    if (
      event.data &&
      typeof event.data === 'object' &&
      event.data.event === 'timeupdate'
    ) {
      const p = Math.round(
        (event.data.currentTime / event.data.duration) * 100
      );
      if (p >= 95) this.onCompleteCallback(this);
    }
  }
}
